/** UI limits and defaults; anything the API also enforces mirrors its DTO (rules/00 §1.5). */
/** Events page size (API max is 200). */
export const EVENTS_PAGE_SIZE = 50;
/** Bounded default range for the event log and the overview tiles, in organisation days (rules/01 §4). */
export const EVENTS_DEFAULT_RANGE_DAYS = 7;
/** Widest range the events list and export accept (M4 §3). */
export const EVENTS_MAX_RANGE_DAYS = 90;
/** Bounded page window for the virtualised log: auto-loading stops here, `maxPages` caps the cache (M4 §4). */
export const EVENTS_MAX_PAGES = 20;
/** "N new events": the newest page is re-read on this interval while the document is visible (rules/01 §3: no per-component socket). */
export const EVENTS_POLL_MS = 30_000;
/**
 * The live stream (M8, rules/01 §3, runbooks/events.md). One `EventSource` per shell writes into the
 * query cache: invalidations are gathered for `LIVE_COALESCE_MS` so a batch of 50 event ids costs one
 * refetch. When the browser gives up on the connection (an HTTP refusal, not a network blip) the
 * portal asks the same route why, aborting after `LIVE_PROBE_MS`, and reopens with a doubling delay
 * from `LIVE_REOPEN_MS` up to `LIVE_REOPEN_MAX_MS`. `retry: 3000` from the api drives the ordinary
 * reconnects; these only cover the case the browser will not retry by itself.
 */
export const LIVE_COALESCE_MS = 500;
export const LIVE_PROBE_MS = 2_000;
export const LIVE_REOPEN_MS = 3_000;
export const LIVE_REOPEN_MAX_MS = 30_000;
/** CSV exports per minute per user, as the API enforces it. */
export const EVENTS_EXPORT_PER_MINUTE = 5;
/** Highest door index the events API filters by. */
export const EVENTS_MAX_DOOR = 15;
/** Recent-events panel on the overview. */
export const OVERVIEW_RECENT_EVENTS = 25;
/** Fallback organisation timezone when /me carries none (ADR-0003). Only this file may name it. */
export const DEFAULT_ORG_TIMEZONE = "Asia/Kolkata";
/**
 * Where the operator types the server details on the terminal; host and port come from
 * GET /devices/pairing-info. The path is quoted from `runbooks/devices.md` "Pairing a terminal" and
 * must stay in step with it — a wrong menu path is a dead end on the customer's first device.
 * Confirmed on the desk ASI6214S (firmware 3.004) on 2026-09-17. The page carries THREE tabs — SDK,
 * CGI and EZAPI — and only CGI is ours; a site that fills in the wrong one gets a terminal that looks
 * configured and never dials in.
 */
export const PAIRING_MENU_PATH = [
  "Communication Settings",
  "Network Settings",
  "Auto Registration",
  "CGI Auto Registration",
] as const;
/**
 * Where the push targets live on the terminal — the page an operator opens when provisioning cannot
 * write them and has to be done by hand (protocol notes §8, "The push targets (only needed when
 * provisioning cannot write them)"). Confirmed on the desk ASI6214S, firmware 3.004, 2026-09-17.
 *
 * Its **Event Type** column is the list ADR-0007's 2026-09-17 addendum calls "we inherit lists we
 * cannot extend": whatever is ticked there is permanently what we receive, so the instructions say
 * All rather than leaving an installer to tick the two they can think of.
 */
export const PUSH_TARGETS_MENU_PATH = ["Communication Settings", "Network Settings", "Auto Upload"] as const;
/**
 * Where the customer switches QR scanning on, in the terminal's own web interface. Quoted verbatim
 * from `01-dahua-protocol-notes.md` §8 ("Prerequisite: QR Code Pass-through must be ON"), established
 * on the desk ASI6214S on firmware 3.004, and it must stay in step with it.
 *
 * It is a constant rather than copy in one component because two screens need the identical words —
 * the device page, and the visitor desk watching scans arrive — and a menu path that says two
 * different things is worse than one that says nothing.
 */
export const QR_PASS_THROUGH_MENU_PATH = [
  "Access Control",
  "Access Control Parameters",
  'Menu = "Back-end Comparison"',
  "QR Code Pass-through",
] as const;
/** Search inputs that hit the API: immediate input state, debounced value in the query key (rules/01 §4). */
export const SEARCH_DEBOUNCE_MS = 300;
/** Pairing dialog: how often to ask whether the terminal has dialled in, and how long to wait for it after pairing. */
export const UNCLAIMED_POLL_MS = 3_000;
export const PAIR_CONNECT_POLL_MS = 3_000;
export const PAIR_CONNECT_TIMEOUT_MS = 60_000;
/** How long "Online" stays on screen before the dialog closes to the device page. */
export const PAIR_ONLINE_LINGER_MS = 1_200;
/** Device page: poll while a job is visibly running (provisioning in progress, waiting for the first sign-in). */
export const DEVICE_DETAIL_POLL_MS = 5_000;
/** Clock drift beyond this is flagged; provisioning re-syncs the clock past the same threshold (M2 plan §1). */
export const CLOCK_DRIFT_WARN_SECONDS = 60;
/** People page size (API default 50, max 200). */
export const PEOPLE_PAGE_SIZE = 50;
/** Person page and device sync card: poll while any device row is pending, syncing or deleting (rules/01 §4). */
export const SYNC_POLL_MS = 5_000;
/**
 * Face photo as the API validates it (M3 §3): the portal downsizes to the longest side, re-encodes as JPEG and
 * steps the quality down until the bytes fit. A face smaller than the minimum is refused before upload.
 */
export const PHOTO_MAX_SIDE_PX = 640;
export const PHOTO_MIN_SIDE_PX = 200;
export const PHOTO_MAX_BYTES = 500 * 1024;
export const PHOTO_JPEG_QUALITIES = [0.85, 0.75, 0.65, 0.5] as const;
/** CSV import (API: at most 2 000 rows, 1 MB). */
export const IMPORT_MAX_ROWS = 2_000;
export const IMPORT_MAX_BYTES = 1_000_000;
/**
 * Attendance (M9 §4, docs/public-api.md "Attendance"). The list and summary take at most a calendar
 * quarter, so any single month always fits and the month picker needs no bound of its own; the page
 * size is fixed by the API at 100 and "Load more" pages by `before` + `beforeId`. Only the export's
 * own limits reach the screen, in the sentence that confirms the download.
 */
export const ATTENDANCE_EXPORT_MAX_RANGE_DAYS = 366;
export const ATTENDANCE_EXPORT_PER_MINUTE = 5;
/** The person page's attendance card: this month's totals plus this many recent organisation days. */
export const ATTENDANCE_CARD_DAYS = 7;
/** How many matches the person filter on Reports offers while you type (it searches, it does not list). */
export const PERSON_PICKER_RESULTS = 8;
/**
 * Client-built report sheets (M12, rules/01 §7 addendum 2026-09-13). A sheet is assembled in the page
 * from the same bounded list routes the screen reads, so the walk needs an end of its own: 20 000 rows is
 * about a thousand people for a month, comfortably inside what Excel, jsPDF and a laptop's memory handle.
 * Past it the file says "first 20,000 rows" on its face rather than quietly being a slice.
 */
export const EXPORT_MAX_ROWS = 20_000;
/** The enrolment snapshot walks people at the API's maximum page size (M12 §1). */
export const ENROLMENT_EXPORT_PAGE_SIZE = 200;
/**
 * The three M12 report routes, mirrored from the api's own DTO file (packages/shared `dto/reports.ts`,
 * rules/00 §1.5). The muster page size is the api's, fixed rather than asked for — it is here so the
 * screen can say how many more people a press of "Load more" brings. The two ranges are what the api
 * refuses past, so the date controls clamp to them rather than letting a 422 be the feedback; the
 * denied cap is the number the truncation notice quotes.
 */
export const MUSTER_PAGE_SIZE = 200;
export const TERMINAL_ACTIVITY_MAX_RANGE_DAYS = 93;
export const DENIED_REPORT_MAX_RANGE_DAYS = 31;
export const DENIED_REPORT_MAX_ROWS = 1_000;
/** Audit trail page size (API default 50, max 200); "Load more" pages by `before`. */
export const AUDIT_PAGE_SIZE = 50;
/** Danger zone: poll the export list while a job is queued or running (rules/01 §4), stop once it settles. */
export const EXPORT_POLL_MS = 5_000;
/** Plan and usage are re-read this often at most; every write that changes usage invalidates them anyway. */
export const USAGE_STALE_MS = 60_000;
/** The grace period before a scheduled deletion runs, as the API applies it (ADR-0009 §6). */
export const TENANT_DELETE_GRACE_DAYS = 30;
/** Invoices page size (API default 20, max 200); the orders table pages the same way. */
export const INVOICES_PAGE_SIZE = 20;
/**
 * The catalogue (M11, ADR-0015). Prices and trial lengths are edited from the internal portal and change
 * rarely, so the list is read once a session; the estimate behind every press of a stepper is debounced
 * like a search box, so a customer holding + costs one round trip, not ten.
 */
export const CATALOGUE_STALE_MS = 5 * 60_000;
export const ESTIMATE_DEBOUNCE_MS = 300;
/**
 * How close a trial has to be to its end before the shell says so. Seven days is a week's notice on a
 * 30-day trial and the same window the api's renewal reminders use for a monthly period (ADR-0015 §5).
 */
export const TRIAL_ENDING_DAYS = 7;
/**
 * How close a cancelled subscription has to be to its end before the shell counts it down (M11 §14). The
 * same week's notice as a trial: long enough to change your mind, short enough not to nag for a year.
 */
export const SUBSCRIPTION_ENDING_DAYS = 7;
/**
 * After a payment the subscription block is immediate, but the plan code and usage come from the
 * 30-second entitlement cache (runbooks/billing.md "As built"), so the tab re-reads on this interval
 * for this long while a change is still pending — a visible job with an end (rules/01 §4).
 */
export const BILLING_POLL_MS = 3_000;
export const BILLING_POLL_WINDOW_MS = 45_000;
/** Endpoints per organisation, as WEBHOOK_MAX_ENDPOINTS defaults on the API (ADR-0013). The URL and
 *  description lengths live with the form schema in `api/contracts.ts` (WEBHOOK_LIMITS). */
export const WEBHOOK_MAX_ENDPOINTS = 5;
/** Delivery log page size (API default 50, max 200); "Load more" pages by `before` + `beforeId`. */
export const DELIVERIES_PAGE_SIZE = 25;
/** A rotated-out secret keeps signing alongside the new one for this long (ADR-0013). */
export const WEBHOOK_SECRET_ROTATION_HOURS = 24;
/** `GET /ledger/outstanding` (API default 50, max 200): the module's own dues list, page/size paged. */
export const LEDGER_DUES_PAGE_SIZE = 50;
/** `GET /gym/members` (M15, api default 50, max 200): the module's own member directory. */
export const GYM_MEMBERS_PAGE_SIZE = 50;
/**
 * `GET /gym/registrations/lookup` (2026-09-21) — "who might this already be", asked while the desk
 * types. The two minimums are the api's own (`gymRegistrationLookupQuery`, rules/00 §1.5): below
 * them the route answers 422, so the dialog simply does not ask rather than making a refusal the
 * feedback. Debounced by `SEARCH_DEBOUNCE_MS` like every other search box.
 */
export const GYM_LOOKUP_MIN_NAME = 2;
export const GYM_LOOKUP_MIN_CONTACT = 3;
/**
 * How close a matched member's membership has to be to its end before the lookup offers a RENEWAL
 * rather than just their page. The widest of the dashboard's three renewal horizons (7 / 15 / 30):
 * inside a month is when a gym starts asking, and offering to renew a contract with five months to
 * run would be noise at the one moment the desk is busiest.
 */
export const GYM_RENEWAL_NUDGE_DAYS = 30;
/** The five ledger reports (M14 §4 wave 3b), mirrored from the api's own DTO file. */
export const LEDGER_REPORT_MAX_RANGE_DAYS = 366;
export const LEDGER_REPORT_PAGE_SIZE = 200;
export const LEDGER_PAYMENTS_MAX_ROWS = 2_500;
/**
 * Documents on a module profile (plans/v2/attachments/README.md §2, §6), mirrored from `@nacs/shared`
 * `media/attachments.ts` — rules/00 §1.5's three layers: the table's CHECKs, the shared constants and
 * Zod schemas, and these. The api decides a file's type by its first bytes and stays the boundary; the
 * portal checks the same things before it sends anything, so a refusal never costs an upload.
 */
export const ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;
/** How many files each profile holds (`ATTACHMENT_LIMITS`); one person with two profiles holds two sets. */
export const ATTACHMENT_LIMITS = {
  staff: 5,
  gym_member: 2,
  gym_trainer: 2,
  student: 5,
  labour: 6,
  // M21 (contracts §13, S11): a co-working member's ID and agreement scans.
  coworking_member: 2,
} as const;
/**
 * How long the files outlive the profile, counted from the moment its end was recorded
 * (`ATTACHMENT_RETENTION_DAYS`, plan §4). The card reads the api's own `purgeAfter` for the date; this
 * is only how it works back to the day the clock started.
 */
export const ATTACHMENT_RETENTION_DAYS = {
  staff: 7,
  gym_member: 30,
  gym_trainer: 30,
  student: 7,
  labour: 30,
  coworking_member: 30,
} as const;
/** The file picker's filter: the extensions for the dialog, the types for browsers that read those. */
export const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
/** `attachmentFileName` (`.trim().min(1).max(120)`): longer names are shortened before upload, keeping the extension. */
export const ATTACHMENT_FILE_NAME_MAX = 120;
/**
 * A 2 MB file on a slow desk connection outlasts the client's 30-second upload timeout, and the
 * progress bar shows it moving, so an attachment is given two minutes before it is called a failure.
 */
export const ATTACHMENT_UPLOAD_TIMEOUT_MS = 120_000;
/** Face Kiosk punch photos are kept this long (`FACE_KIOSK_LIMITS.photoRetentionDays`, M24 contracts §3). */
export const FACE_KIOSK_PHOTO_RETENTION_DAYS = 90;
